import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Collapse,
  Stack,
  Grow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Send as ApplyIcon } from '@mui/icons-material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FiltersHeader from './FiltersHeader';
import AddRemoveFilters from './AddRemoveFilters';
import { LoadingButton } from '../LoadingButton';
import { FilterChip, FilterFormLabel } from '../FilterAndSearchPanel/styles';
import { Filter, FilterOption, FilterType, SelectedFiltersType } from '@/types';
import { autoCompleteInputStyles, autoCompleteStyles, filtersContainerStyles } from './styles';
import { containedButtonStyle } from '@/theme';

type Props = {
  filtersLimit?: number;
  filters: Filter[];
  loading?: boolean;
  disabled?: boolean;
  onApply: (filters: Record<string, string[]>) => void;
  autoApply?: boolean;
  handleSearch?: (value: string) => void;
  showResetButton?: boolean;
  rightComponent?: React.ReactNode;
  filterInputWidth?: string;
  initialSelectedFilters?: SelectedFiltersType;
};

export const FiltersPanel = ({
  filtersLimit = 5,
  filters = [],
  loading = false,
  disabled = false,
  onApply,
  autoApply = true,
  showResetButton = false,
  rightComponent = <></>,
  filterInputWidth = '18.5rem',
  initialSelectedFilters = {},
  handleSearch,
}: Props) => {
  const [displayFilters, setDisplayFilters] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersType>({});
  const [toggledFilters, setToggledFilters] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (Object.keys(initialSelectedFilters).length > 0) {
      setSelectedFilters(initialSelectedFilters);
    }
  }, [initialSelectedFilters]);

  const initialFilters = useMemo(() => filters.slice(0, filtersLimit), [filters, filtersLimit]);

  const visibleFilters = useMemo(() => {
    const filterMap = new Map<string, Filter>();

    initialFilters.forEach((filter) => {
      if (toggledFilters[filter.key] !== false) {
        filterMap.set(filter.key, filter);
      }
    });

    filters.forEach((filter) => {
      if (toggledFilters[filter.key]) {
        filterMap.set(filter.key, filter);
      }
    });

    return [...filterMap.values()];
  }, [filters, initialFilters, toggledFilters]);

  const filtersCount = useMemo(() => {
    return Object.values(selectedFilters).flat().length || 0;
  }, [selectedFilters]);

  const mappedFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(selectedFilters).map(([key, value]) => [key, value.map((item) => item.key)])
    );
  }, [selectedFilters]);

  useEffect(() => {
    if (autoApply && Object.values(selectedFilters).flat().length > 0) {
      onApply(mappedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoApply, selectedFilters]);

  const handleDisplayFilters = () => setDisplayFilters(!displayFilters);

  const handleFiltersChange = (filterKey: string, values: FilterOption | FilterOption[] | null) => {
    setSelectedFilters((previousSelectedFilters) => {
      const updatedFilters = { ...previousSelectedFilters };
      const normalizedValues = Array.isArray(values) ? values : values ? [values] : undefined;
      if (!normalizedValues || normalizedValues.length === 0) {
        delete updatedFilters[filterKey];
        if (Object.values(updatedFilters).flat().length === 0) onApply({});
        return updatedFilters;
      }
      updatedFilters[filterKey] = normalizedValues;
      return updatedFilters;
    });
  };

  const handleToggleFilter = (filter: Filter) => {
    const toggleState = toggledFilters[filter.key];

    setToggledFilters((previous) => ({
      ...previous,
      [filter.key]: !toggleState,
    }));

    if (toggleState) {
      setSelectedFilters((previous) => {
        const updatedFilters = { ...previous };
        delete updatedFilters[filter.key];
        return updatedFilters;
      });
    }
  };

  const handleResetFilters = () => {
    setDisplayFilters(false);
    setToggledFilters({});
    setSelectedFilters({});
    onApply({});
  };

  const handleApply = () => onApply(mappedFilters);

  return (
    <Box sx={{ my: 2 }}>
      <FiltersHeader
        displayFilters={displayFilters}
        filtersCount={filtersCount}
        handleDisplayFilters={handleDisplayFilters}
        handleResetFilters={handleResetFilters}
        handleSearch={handleSearch}
        showResetButton={showResetButton}
        rightComponent={rightComponent}
      />

      <Collapse in={displayFilters} sx={{ position: 'relative' }}>
        <Stack direction="row" sx={filtersContainerStyles} alignItems="flex-end">
          {visibleFilters.length === 0 && (
            <Typography variant="subtitle2">No filters available</Typography>
          )}
          {visibleFilters.map(({ key, label, type, options }) => {
            const isMultiSelect = type === FilterType.MULTI_SELECT;

            let selectedValue;
            if (isMultiSelect) selectedValue = selectedFilters?.[key] ?? [];
            else selectedValue = selectedFilters?.[key]?.[0] ?? null;

            return (
              <Grow in key={key}>
                <Box>
                  <FilterFormLabel>{label}</FilterFormLabel>
                  <Autocomplete
                    size="small"
                    multiple={isMultiSelect}
                    disableCloseOnSelect={isMultiSelect}
                    options={options}
                    value={selectedValue}
                    onChange={(_, newValue) => handleFiltersChange(key, newValue)}
                    getOptionLabel={(option) => option.label}
                    noOptionsText="No Options"
                    renderTags={(value, getTagProps) => {
                      const tagsLength = value.length;
                      const limitTags = 1;
                      const tooltipTitle = value
                        .map((item) => item.label)
                        .slice(limitTags)
                        .join(', ');

                      return (
                        <>
                          {value.slice(0, limitTags).map((option, index) => (
                            <FilterChip
                              {...getTagProps({ index })}
                              key={option.key}
                              label={option.label}
                              size="small"
                            />
                          ))}
                          {tagsLength > limitTags && (
                            <Tooltip arrow title={tooltipTitle} placement="top">
                              <FilterChip
                                sx={{ cursor: 'pointer' }}
                                label={`+${tagsLength - limitTags}`}
                                size="small"
                              />
                            </Tooltip>
                          )}
                        </>
                      );
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li style={{ padding: '0px 8px', minHeight: 30 }} {...props}>
                        {isMultiSelect && (
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" color="info" />}
                            sx={{ marginRight: 1 }}
                            checked={selected}
                          />
                        )}
                        {option.label}
                      </li>
                    )}
                    renderInput={(parameters) => (
                      <TextField
                        {...parameters}
                        InputLabelProps={{ sx: { py: '3px' } }}
                        InputProps={{ ...parameters.InputProps, sx: autoCompleteInputStyles }}
                        placeholder={selectedFilters[key]?.length ? '' : `Select ${label}`}
                      />
                    )}
                    sx={{ ...autoCompleteStyles, minWidth: filterInputWidth }}
                  />
                </Box>
              </Grow>
            );
          })}
          {filters.length > filtersLimit && (
            <AddRemoveFilters
              filters={filters}
              filtersLimit={filtersLimit}
              visibleFilters={visibleFilters}
              handleToggleFilter={handleToggleFilter}
            />
          )}

          {!autoApply && visibleFilters.length > 0 && (
            <LoadingButton
              buttonText="Apply"
              buttonVariant="contained"
              onClick={handleApply}
              loading={loading}
              disabled={filtersCount === 0 || disabled}
              buttonStyles={{ ...containedButtonStyle, height: 30, minWidth: 85 }}
              startIcon={<ApplyIcon />}
            />
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};
